import { useRecoilState, useRecoilValue } from 'recoil';
import { AtWorkLocation, IProfile, UserStatus } from './types';
import { sCanUseStatus, sProfile, sStatusSnapshot } from 'shared-state/identity/selectors';
import type { IStatusSnapshot, IStoredStatus } from 'shared-state/identity/statusPersistence';
import {
    aIdentityClaims,
    aProfileImage,
    aUserStatus,
    aAtWorkLocation,
    aAtWorkCustomPhrase,
    aAwayStatusMessage,
    aFocusEndTime,
    aFocusDurationMinutes,
    aFocusTimeKnockDoor,
    aFocusTimeTextMessage,
    aShowFocusExtendEndModal,
    aShowOnCallEndedModal,
    aShowAwayExtensionModal,
    aAwaySinceTimestamp,
    aOutOfOfficeReturnDate,
    aOutOfOfficeReturnTime,
    aOutOfOfficeAbsenceComment,
    aOutOfOfficePreferredMethods,
    aOtherUsersStatusMap,
    IOutOfOfficePreferredMethods,
    aOtherUsersStatusFromServer
} from './atoms';

export const useLocalProfile = (): IProfile | undefined => {
    return useRecoilValue(sProfile);
};

export const useIdentityClaims = (): any => {
    return useRecoilState(aIdentityClaims);
};

export const useLocalProfileImage = (): any => {
    return useRecoilState(aProfileImage);
};

export const useUserStatus = (): [UserStatus | undefined, (status: UserStatus) => void] => {
    return useRecoilState(aUserStatus);
};

export const useAtWorkLocation = (): [AtWorkLocation, (location: AtWorkLocation) => void] => {
    return useRecoilState(aAtWorkLocation);
};

export const useAtWorkCustomPhrase = (): [string | undefined, (phrase: string | undefined) => void] => {
    return useRecoilState(aAtWorkCustomPhrase);
};

export const useAwayStatusMessage = (): [string | undefined, (message: string | undefined) => void] => {
    return useRecoilState(aAwayStatusMessage);
};

export const useFocusEndTime = (): [number | undefined, (timestamp: number | undefined) => void] => {
    return useRecoilState(aFocusEndTime);
};

export const useFocusDurationMinutes = (): [number | undefined, (minutes: number | undefined) => void] => {
    return useRecoilState(aFocusDurationMinutes);
};

export const useFocusTimeKnockDoor = (): [boolean, (value: boolean) => void] => {
    return useRecoilState(aFocusTimeKnockDoor);
};

export const useFocusTimeTextMessage = (): [boolean, (value: boolean) => void] => {
    return useRecoilState(aFocusTimeTextMessage);
};

export const useShowFocusExtendEndModal = (): [boolean, (show: boolean) => void] => {
    return useRecoilState(aShowFocusExtendEndModal);
};

export const useShowOnCallEndedModal = (): [boolean, (show: boolean) => void] => {
    return useRecoilState(aShowOnCallEndedModal);
};

export const useShowAwayExtensionModal = (): [boolean, (show: boolean) => void] => {
    return useRecoilState(aShowAwayExtensionModal);
};

export const useAwaySinceTimestamp = (): [number | undefined, (ts: number | undefined) => void] => {
    return useRecoilState(aAwaySinceTimestamp);
};

export const useOutOfOfficeReturnDate = (): [Date | undefined, (d: Date | undefined) => void] => {
    return useRecoilState(aOutOfOfficeReturnDate);
};
export const useOutOfOfficeReturnTime = (): [string | null | undefined, (t: string | null | undefined) => void] => {
    return useRecoilState(aOutOfOfficeReturnTime);
};
export const useOutOfOfficeAbsenceComment = (): [string, (s: string) => void] => {
    return useRecoilState(aOutOfOfficeAbsenceComment);
};
export const useOutOfOfficePreferredMethods = (): [IOutOfOfficePreferredMethods, (m: IOutOfOfficePreferredMethods) => void] => {
    return useRecoilState(aOutOfOfficePreferredMethods);
};

/**
 * Single hook for full status: status, phrase, location, duration, returnDate, absenceComment, emergencyContact.
 * Data from Recoil atoms (hydrated from IndexedDB by StatusPersistenceProvider).
 */
export const useStatusSnapshot = (): IStatusSnapshot | null => useRecoilValue(sStatusSnapshot);

/** False for visitors: they have no status feature at all. */
export const useCanUseStatus = (): boolean => useRecoilValue(sCanUseStatus);

/**
 * Hook to read the entire map of other users' statuses (userId -> IStoredStatus)
 */
export const useOtherUsersStatusMap = (): Record<string, IStoredStatus> => {
    return useRecoilValue(aOtherUsersStatusMap);
};

/**
 * Hook to read a specific other user's status by userId
 */
export const useOtherUsersStatus = (userId: string): IStoredStatus | null => {
    const statusMap = useOtherUsersStatusMap();
    return statusMap[userId] ?? null;
};

/**
 * Hook to update the other users' status map
 */
export const useSetOtherUsersStatusMap = (): ((
    map: Record<string, IStoredStatus> | ((prev: Record<string, IStoredStatus>) => Record<string, IStoredStatus>)
) => void) => {
    const [, setMap] = useRecoilState(aOtherUsersStatusMap);
    return setMap;
};

export const useSetOtherUsersStatusFromServer = (): any => {
    return useRecoilState(aOtherUsersStatusFromServer);
};
